import React, { useState, useEffect } from 'react';
import { DatePicker, Select, Table, Row, Col, Card, Button, message } from 'antd';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import { exportSubcontractorBillingPdf, getSubcontractors, getSubcontractorBilling } from '../api';

const { Option } = Select;

const SubcontractorBilling = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [selectedSubcontractor, setSelectedSubcontractor] = useState();
  const [data, setData] = useState([]); // Placeholder for billing data
  const [subcontractors, setSubcontractors] = useState([]);
  const [loadingSubcontractors, setLoadingSubcontractors] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const columns = [
    { title: 'Employee', dataIndex: 'employee', key: 'employee' },
    { title: 'Subcontractor', dataIndex: 'subcontractor', key: 'subcontractor' },
    { title: 'Hours Worked', dataIndex: 'hours_worked', key: 'hours_worked' },
    { title: 'Hourly Rate', dataIndex: 'hourly_rate', key: 'hourly_rate', render: v => `AED ${v}` },
    { title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount', render: v => `AED ${v}` },
  ];

  // Calculate total hours and average hourly rate
  const totalHours = Array.isArray(data) ? data.reduce((sum, row) => sum + (Number(row.hours_worked) || 0), 0) : 0;
  const avgRate = Array.isArray(data) && data.length > 0 ? (data.reduce((sum, row) => sum + (Number(row.hourly_rate) || 0), 0) / data.length) : 0;

  const handleDownloadPdf = async () => {
    if (!selectedSubcontractor || !selectedMonth) {
      message.warning('Please select a subcontractor and month first.');
      return;
    }
    setDownloading(true);
    try {
      const blob = await exportSubcontractorBillingPdf({ subcontractor_id: selectedSubcontractor, month: selectedMonth.format('YYYY-MM') });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `subcontractor_billing_${selectedSubcontractor}_${selectedMonth.format('YYYY-MM')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setLoadingSubcontractors(true);
    getSubcontractors().then(res => {
      if (res.success) setSubcontractors(res.data);
      else message.error(res.message || 'Failed to fetch subcontractors');
      setLoadingSubcontractors(false);
    });
  }, []);

  useEffect(() => {
    if (selectedSubcontractor && selectedMonth) {
      setLoadingData(true);
      getSubcontractorBilling({ subcontractor_id: selectedSubcontractor, month: selectedMonth.format('YYYY-MM') })
        .then(res => {
          if (res.data.success && Array.isArray(res.data.data)) setData(res.data.data);
          else setData([]);
          setLoadingData(false);
        })
        .catch(() => {
          setData([]);
          setLoadingData(false);
        });
    } else {
      setData([]);
    }
  }, [selectedSubcontractor, selectedMonth]);

  return (
    <Card title="Subcontractor Billing">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 160 }}
          />
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="Select Subcontractor"
            value={selectedSubcontractor}
            onChange={setSelectedSubcontractor}
            style={{ width: 200 }}
            loading={loadingSubcontractors}
            showSearch
            optionFilterProp="children"
          >
            {subcontractors.map(sc => (
              <Option key={sc.id} value={sc.id}>{sc.name}</Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPdf} loading={downloading}>
            Download PDF
          </Button>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 8 }}>
        <Col>
          <Card size="small" style={{ display: 'inline-block', marginRight: 16 }}>
            <b>Total Hours:</b> {totalHours}
          </Card>
          <Card size="small" style={{ display: 'inline-block' }}>
            <b>Average Hourly Rate:</b> AED {avgRate.toFixed(2)}
          </Card>
        </Col>
      </Row>
      <Table columns={columns} dataSource={data} rowKey="employee" bordered loading={loadingData} />
    </Card>
  );
};

export default SubcontractorBilling; 